import {
  type BodyType,
  type Drive,
  type FuelType,
  type Steering,
  type Transmission,
  type VehicleAvailability,
  type VehicleListing,
} from '@/services/vehicles/types';

export const fuelLabels: Record<FuelType, string> = {
  petrol: 'Petrol',
  diesel: 'Diesel',
  hybrid: 'Hybrid',
  plug_in_hybrid: 'Plug-in hybrid',
  electric: 'Electric',
  lpg: 'LPG',
  other: 'Other',
};

export const transmissionLabels: Record<Transmission, string> = {
  automatic: 'Automatic',
  manual: 'Manual',
  cvt: 'CVT',
  other: 'Other',
};

export const driveLabels: Record<Drive, string> = { '2wd': '2WD', '4wd': '4WD', awd: 'AWD' };

export const steeringLabels: Record<Steering, string> = {
  right: 'Right-hand drive',
  left: 'Left-hand drive',
};

export const bodyTypeLabels: Record<BodyType, string> = {
  sedan: 'Sedan',
  suv: 'SUV',
  hatchback: 'Hatchback',
  wagon: 'Wagon',
  coupe: 'Coupe',
  convertible: 'Convertible',
  minivan: 'Minivan',
  van: 'Van',
  pickup: 'Pickup',
  truck: 'Truck',
  bus: 'Bus',
  other: 'Other',
};

export const availabilityLabels: Record<VehicleAvailability, string> = {
  available: 'Available',
  reserved: 'Reserved',
  sold: 'Sold',
};

const regionNames =
  typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

/** 'JP' → 'Japan'. Falls back to the code. */
export function countryName(code: string): string {
  try {
    return regionNames?.of(code) ?? code;
  } catch {
    return code;
  }
}

const integer = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export function formatMileage(km: number | null): string | null {
  return km === null ? null : `${integer.format(km)} km`;
}

/** 1998 cc → '2.0L'. */
export function formatEngine(cc: number | null): string | null {
  return cc === null ? null : `${(cc / 1000).toFixed(1)}L`;
}

export function formatUsd(amount: number): string {
  return `USD ${integer.format(amount)}`;
}

export function formatUgx(amount: number): string {
  return `UGX ${integer.format(amount)}`;
}

/** 2026-09-15 → '15 Sep 2026'. */
export function formatDate(isoDate: string): string {
  const date = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

export function vehicleTitle(v: Pick<VehicleListing, 'make' | 'model' | 'year'>): string {
  return `${v.make} ${v.model} ${v.year}`;
}

export function vehiclePath(v: Pick<VehicleListing, 'slug'>): string {
  return `/vehicles/${v.slug}`;
}
